import { Box, FormControl, InputLabel, MenuItem, Select, Stack } from "@mui/material";
import type {
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowParams,
  GridSortModel,
} from "@mui/x-data-grid";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { BrowserSessionStatusChip } from "../components/browser-session-status-chip";
import { CreatedByColumn } from "../components/created-by-column";
import { DataGrid } from "../components/data-grid";
import { PageHeader } from "../components/page-header";
import { useApiQuery } from "../hooks/use-api-query";
import { useUrlFilters } from "../hooks/use-url-filters";
import type {
  BrowserSession,
  BrowserSessionListResponse,
  BrowserSessionStatus,
} from "../types/browser-session";
import { formatDurationFromTimes } from "../utils";

const columns: GridColDef<BrowserSession>[] = [
  {
    field: "status",
    headerName: "Status",
    width: 130,
    sortable: true,
    renderCell: (params: GridRenderCellParams<BrowserSession>) => {
      return <BrowserSessionStatusChip status={params.row.status} />;
    },
  },
  {
    field: "current_url",
    headerName: "Current URL",
    minWidth: 300,
    flex: 1,
    sortable: false,
    renderCell: (params: GridRenderCellParams<BrowserSession>) => {
      const url = params.row.current_url;
      if (!url) return "-";
      // Truncate long URLs
      return (
        <Box
          component="span"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={url}
        >
          {url}
        </Box>
      );
    },
  },
  {
    field: "created_at",
    type: "dateTime",
    headerName: "Created",
    valueGetter: (val: BrowserSession["created_at"]) => {
      if (!val) return null;
      return new Date(val);
    },
    width: 180,
    sortable: true,
  },
  {
    field: "created_by",
    headerName: "Created by",
    width: 180,
    sortable: false,
    renderCell: (params: GridRenderCellParams<BrowserSession>) =>
      params.row.created_by ? (
        <CreatedByColumn
          {...(params as GridRenderCellParams<BrowserSession & { created_by: string }>)}
        />
      ) : (
        "-"
      ),
  },
  {
    field: "last_action_at",
    type: "dateTime",
    headerName: "Last Action",
    valueGetter: (val: BrowserSession["last_action_at"]) => {
      if (!val) return null;
      return new Date(val);
    },
    width: 180,
    sortable: true,
  },
  {
    field: "duration",
    type: "string",
    headerName: "Duration",
    valueGetter: (_, row) => {
      return formatDurationFromTimes(row?.created_at, row?.last_action_at);
    },
    width: 100,
    sortable: false,
  },
  {
    field: "config.viewport_type",
    headerName: "Viewport",
    width: 100,
    sortable: false,
    valueGetter: (_, row) => {
      return row.config?.viewport_type ?? "-";
    },
  },
];

const statusOptions: Array<{
  value: BrowserSessionStatus | "";
  label: string;
}> = [
  { value: "", label: "All Statuses" },
  { value: "starting", label: "Starting" },
  { value: "active", label: "Active" },
  { value: "closing", label: "Closing" },
  { value: "closed", label: "Closed" },
  { value: "error", label: "Error" },
];

export default function BrowserSessionsPage() {
  const [filters, setFilters] = useUrlFilters({
    status: "",
  });
  const nav = useNavigate();
  const [paginationState, setPaginationState] = useState({
    page: 0,
    pageSize: 25,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  // Build query string with pagination and filters
  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("limit", paginationState.pageSize.toString());
    params.set("offset", (paginationState.page * paginationState.pageSize).toString());

    if (sortModel.length > 0) {
      const sortItem = sortModel[0];
      params.set("order_by", sortItem.field);
      params.set("order_direction", sortItem.sort || "desc");
    }

    if (filters.status) {
      params.set("status", filters.status);
    }

    return `/browser-sessions?${params.toString()}`;
  }, [paginationState, sortModel, filters.status]);

  const { data, isLoading } = useApiQuery<BrowserSessionListResponse>(queryString);

  // Reset pagination when filters change
  const handleFilterChange = (newStatus: string) => {
    setPaginationState((prev) => ({ ...prev, page: 0 }));
    setFilters({ status: newStatus });
  };

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    setPaginationState({
      page: model.page,
      pageSize: model.pageSize,
    });
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
  };

  const handleRowClick = (params: GridRowParams<BrowserSession>) => {
    nav(`/browser-sessions/${params.row.id}`);
  };

  return (
    <Box
      data-testid="browser-sessions-page"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "85vh",
        overflow: "hidden",
      }}
    >
      <PageHeader
        title="Browser Sessions"
        subtitle="View browser sessions created via the Browser Protocol API"
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              value={filters.status}
              label="Status"
              onChange={(e) => handleFilterChange(e.target.value)}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </PageHeader>

      <DataGrid
        loading={isLoading}
        rows={data?.sessions ?? []}
        columns={columns}
        pagination
        paginationMode="server"
        paginationModel={paginationState}
        onPaginationModelChange={handlePaginationModelChange}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        pageSizeOptions={[10, 25, 50, 100]}
        rowCount={data?.total ?? -1}
        containerSx={{
          height: "100%",
          "& .MuiDataGrid-row": {
            cursor: "pointer",
          },
        }}
        onRowClick={handleRowClick}
      />
    </Box>
  );
}
